import { Field, useFormikContext } from 'formik';
import React, { FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import useFocusOnRouteChange from '../../hooks/useFocusOnRouteChange';
import { getApbOptions } from '../../models/APBOptions';
import { ListQuery } from '../../models/ListQuery';
import { getControllerNames } from '../../services/controllerService';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import { validateItemName } from '../../utilities/Validation';
import EntityHierarchy from '../entityHierarchy/entityHierarchy';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import SettingSelectField from '../settingFields/SettingSelectField';
import Togglebox from '../togglebox/togglebox';

interface ZoneProps {
    setFormikContext: (formikContext) => void
}

export const ZoneCard: FC<ZoneProps> = ({ setFormikContext }) => {
    const formikContext = useFormikContext<any>();
    const { translate } = useLocale();
    const siteId = useSelector((state: IAppState) => state.site.site.value)

    const inputRef = useRef<HTMLInputElement>(null);
    useFocusOnRouteChange(inputRef);

    useEffect(() => {
        setFormikContext(formikContext)
    }, [])

    //When global site selector is change make sure you clear field that depend on it
    useEffect(() => {
        if (!!!formikContext.values.id) {
            formikContext.setFieldValue("controllerId", "");
        }
    }, [siteId])

    return (
        <>
            <div className="w-full p-2 inline-flex justify-start items-center bg-white">
                {formikContext.values.id ?
                    (<div className="ml-8 mr-5 card-details">
                        <EntityHierarchy title={formikContext.values.name} tree={{ label: formikContext.values.controller, node: { label: formikContext.values.name } }} />
                    </div>) : ""}
                <div className="flex flex-col py-4 px-8">
                    <div className="mb-2">
                        <div className="flex flex-row justify-between" id="zoneNameLabel">
                            <Label
                                htmlFor="name"
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                            {formikContext.errors.name ? <span className='text-red-600 text-xs'>{formikContext.errors.name}</span> : null}
                        </div>
                        <div className="flex flex-row justify-between">
                            <Field
                                id="zoneNameInputId"
                                type="text"
                                name="name"
                                innerRef={inputRef}
                                className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                                validate={(value) => validateItemName(value, translate)} />
                        </div>
                    </div>
                    <div className="mb-2" id='controllerSelectionDiv'>
                        <div className="flex flex-row justify-between" id='controllerLabelId'>
                            <Label htmlFor='controllerId'
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.controller)}:</Label>
                            {formikContext.errors.controllerId ? <span className='text-red-600 text-xs'>{formikContext.errors.controllerId}</span> : null}
                        </div>
                        {formikContext.values.id ?
                            <Field
                                value={formikContext.values.controller}
                                disabled={true}
                                className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" /> :
                            <PaginatedSearchableSelectField
                                name={translate.byKey(TranslationKeys.controller)}
                                key="controllerId"
                                accessor='controllerId'
                                dataMethod={(listParameters?: ListQuery) => getControllerNames({ ...listParameters, filters: appendFilter(formikContext.values.siteId ?? siteId, 'Site id', 'site.id', listParameters?.filters ?? []) })}
                                searchAccessor={'devName'}
                                required={true}
                                dependent={{ accessor: "siteId", key: "site.id", name: "Site", value: formikContext.values.id ? formikContext.values.siteId : siteId, childAcceccor: "controllerId" }}
                                onSelect={(event, item) => {
                                    formikContext.setFieldValue('controller', item?.label);
                                    formikContext.setFieldValue('controllerId', item?.value);
                                }}
                                selectedItemLabel={formikContext.values.controller} />}
                    </div>
                </div>
                <div className="flex flex-col py-4 px-8">
                    <div className="mb-2">
                        <div id="antiPassBackDropDownInputId">
                            <SettingSelectField
                                key="antiPassBackSelectField"
                                accessor="apbKey"
                                displayName={translate.byKey(TranslationKeys.antiPassback)}
                                options={getApbOptions(translate)}
                                required
                                className="">
                            </SettingSelectField>
                        </div>
                    </div>
                    <Label
                        htmlFor="relaxedTimeExit"
                        className="text-xs text-gray-600 mt-1 my-1">{translate.byKey(TranslationKeys.relaxedTimeExit)}:</Label>
                    <div className="my-0">
                        <Togglebox name="relaxedTimeExit" label="" />
                    </div>
                </div>
            </div>
        </>
    );
}
